
import SvgRangeCalendar from '@/assets/icons/range-calendar.svg';
import SvgTrash from "@/assets/icons/trash.svg";
import TrashBtn from "@/components/base/buttons/TrashBtn.vue";
import TimePicker from '@/components/base/pickers/TimePicker.vue';
import { eventBus } from "@/eventbus";
import {
    ComparePeriod,
    getCompareOffsetInMonths,
    getComparePeriodByGap,
    getComparePeriodByMonths,
    getComparePeriodFromStart,
    isFullCalendarMonth,
} from '@/utils/comparePeriod';
import {
    formatDateInputPicker,
    getCurrentDate,
    getCurrentWeek, getCurrentYear,
    getFormatDate,
    getFullCurrentMonth,
    getFullCurrentWeek, getFullPreviousMonth,
    getFullPreviousWeek,
    getPreviousDate,
} from '@/utils/formatDates';
import { parseISO, subDays } from 'date-fns';
import pullAll from 'lodash-es/pullAll';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import { Component, Prop, PropSync, Ref, Vue, Watch } from 'vue-property-decorator';
import BaseCheckbox from '../../../components-UI/BaseCheckbox.vue';

enum DATE_TEMPLATE {
    TODAY = 'Сегодня',
    YESTERDAY = 'Вчера',
    LAST_SEVEN_DAYS = 'Последние 7 дней',
    CURRENT_WEEK = 'За текущую неделю',
    LAST_WEEK = 'За прошлую неделю',
    CURRENT_MONTH = 'За текущий месяц',
    LAST_MONTH = 'За прошлый месяц',
    CURRENT_YEAR = 'За текущий год',
    RANDOM_PERIOD = 'Произвольный период',
}

type TDateRange = {
    start: any;
    end: any;
};

@Component({
    components: {
        SvgTrash,
        TrashBtn,
        DatePicker, SvgRangeCalendar,
        TimePicker,
        BaseCheckbox,
    },
})
export default class DateRange extends Vue {
    @PropSync('filter', { required: true }) filterSync!: string[];
    @PropSync('period') periodTemplate!: string;
    @PropSync('compareFilter') compareFilterSync!: string[];
    @PropSync('isCompare') isCompareSync!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isCompareMode!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isDisabledSubmit!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isTrashBtn!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isIconActivator!: boolean;
    @Prop({ type: Boolean, default: true }) readonly isSubmitBtn!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isTop!: boolean;
    @Prop({ type: Boolean, default: false }) readonly clearable!: boolean;
    @Prop({ type: String, default: 'data-range__content' }) readonly contentClass!: string;
    @Prop({ type: String, default: '' }) readonly label!: string;
    @Prop({ type: String, default: '' }) readonly labelFooterInput!: string;
    @Prop({ type: String, default: 'id' }) readonly id!: string;
    @Prop({ type: String, default: 'Сформировать' }) readonly submitBtnText!: string;
    @Prop({ type: Boolean, default: true }) readonly isUpdateAfterClear!: boolean;
    @Prop({ default: '.data-range__input-calendar-false' }) attachClass!: string | boolean;
    @Prop({ type: Boolean, default: false }) readonly isOffset!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isCompare!: boolean;
    @Prop({ type: Array, default: () => [] }) readonly periodForCompare!: string[];
    @Prop({ type: Boolean, default: false }) readonly isOnlyPlaceholder!: boolean;
    @Prop({ type: Array, default: () => [] }) readonly excludingTemplate!: string[];
    @Prop({ type: Boolean, default: false }) readonly isFullWidth!: boolean;
    @Prop({
        type: Function,
        default: () => ({ start: null, end: null }),
    }) readonly availableDates!: Function;
    @Prop({}) disabledDates!: any;

    @Ref('calendar') calendar!: any;

    isMenuMounted = false;
    time = {
        hour: 0,
        minutes: 0,
    };
    menu: boolean = false;
    currentDateTemplate: string = '';
    dateTemplate: string[] = [];
    formatInput = formatDateInputPicker;
    allowedDates: (date?: TDateRange | null, drag?: any) => TDateRange = () => ({ start: null, end: null });
    getAvailableDates: null | TDateRange = this.allowedDates({ start: null, end: null });
    drag: TDateRange = { start: null, end: null };
    isCompareInFocus = false;

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width <= 768;
    }

    get randomPeriod(): string {
        return DATE_TEMPLATE.RANDOM_PERIOD;
    }

    get isDisabled(): boolean {
        return this.isDisabledSubmit && this.getPickerValue.start === null;
    }

    get getAvailableDatesHandler(): TDateRange {
        if (this.isCompareInFocus) {
            const end = subDays(new Date(this.filterSync[0]), 1);
            return { start: this.getAvailableDates!.start, end };
        } else {
            return this.getAvailableDates || { start: null, end: null };
        }
    }

    get getPickerValue(): TDateRange {
        if (this.isCompareInFocus) {
            if (!this.compareFilterSync || this.compareFilterSync.length <= 0) {
                return { start: null, end: null };
            }
            const start = new Date(this.compareFilterSync[0]);
            if (this.compareFilterSync.length === 1) {
                return { start, end: start };
            }
            const end = new Date(this.compareFilterSync[1]);
            return { start, end };
        } else {
            if (!this.filterSync || this.filterSync.length <= 0) {
                return { start: null, end: null };
            }
            const start = new Date(this.filterSync[0]);
            if (this.filterSync.length === 1) {
                return { start, end: start };
            }
            const end = new Date(this.filterSync[1]);
            return { start, end };
        }
    }

    toggleMenu(e: boolean): void {
        this.$emit('toggle-menu', e);
    }

    @Watch('$props.availableDates', { immediate: true })
    watchApiMethod(value: any): void {
        this.allowedDates = value;
        this.updateAvailableDates();
    }

    get input(): string {
        if (!this.filterSync || this.filterSync.length <= 0) {
            return '';
        }
        const start = formatDateInputPicker(this.filterSync[0]);
        if (this.filterSync.length === 1) {
            return `${start}-${start}`;
        }
        const end = formatDateInputPicker(this.filterSync[1]);
        return `${start}-${end}`;
    }

    get compareInput(): string {
        if (!this.compareFilterSync || this.compareFilterSync.length <= 0) {
            return '';
        }
        const start = formatDateInputPicker(this.compareFilterSync[0]);
        if (this.compareFilterSync.length === 1) {
            return `${start}-${start}`;
        }
        const end = formatDateInputPicker(this.compareFilterSync[1]);
        return `${start}-${end}`;
    }

    created(): void {
        eventBus.$on('check-allowed-dates', this.checkAllowedDates);
    }

    beforeDestroy(): void {
        eventBus.$off('check-allowed-dates', this.checkAllowedDates);
    }

    checkAllowedDates(dates: any): void {
        this.getAvailableDates = dates;
    }

    mounted(): void {
        if (!!this.getPickerValue.start) {
            this.getCurrentDateTemplate(this.getPickerValue);
        }

        this.dateTemplate = pullAll(Object.values(DATE_TEMPLATE), this.excludingTemplate);
    }

    clearDate(): void {
        this.filterSync = [];
        this.currentDateTemplate = '';
        if (this.isCompareSync) {
            this.filterSync = [getCurrentDate('yyyy-MM-dd'), getCurrentDate('yyyy-MM-dd')];
            this.setCompareRange(getCurrentDate('yyyy-MM-dd'), getCurrentDate('yyyy-MM-dd'));
        }
        if (this.isUpdateAfterClear) {
            this.$emit('update', null);
        }
    }

    getCurrentDateTemplate(date: { start: string | Date, end: string | Date }): void {
        this.currentDateTemplate = this.switchDateTemplate([
            getFormatDate(date.start, 'yyyy-MM-dd'),
            getFormatDate(date.end, 'yyyy-MM-dd'),
        ]);
        this.periodTemplate = this.currentDateTemplate;
    }

    @Watch('isCompareSync')
    updateCompareSync(): void {
        if (this.isCompareSync) {
            this.setCompareRange(this.filterSync[0], this.filterSync[1] || this.filterSync[0]);
        }
    }

    onCompareCheckboxChange(value: boolean): void {
        if (value) {
            this.setCompareRange(this.filterSync[0], this.filterSync[1] || this.filterSync[0]);
        }
        this.isCompareSync = value;
        if (value) {
            this.$nextTick(() => {
                this.$emit('update-compare');
            });
        }
    }

    async submitDate(): Promise<void> {
        if (this.isCompareInFocus || this.isCompareSync) {
            await this.$emit('update-compare');
        } else {
            await this.$emit('update', this.filterSync);
        }
        this.menu = false;
        this.toggleMenu(false);
    }

    updateAvailableDates(): void {
        this.getAvailableDates = this.allowedDates(this.getPickerValue, this.drag);
    }

    changeInput(day: any): void {
        if (this.calendar.isDragging && !this.isCompareInFocus) {
            return;
        }

        if (this.isCompareInFocus) {
            const start = this.filterSync[0];
            const end = this.filterSync[1] || this.filterSync[0];
            this.setCompareRange(start, end, day.id);
        } else {
            const start = getFormatDate(this.drag.start, 'yyyy-MM-dd');
            const end = getFormatDate(this.drag.end, 'yyyy-MM-dd');
            this.filterSync = [start, end];
            // держим periodTemplate в актуальном состоянии: при ручном выборе дат
            // он должен становиться "Произвольный период", иначе сохранение отчёта
            // воспримет период как пресет и запишет сдвиг вместо абсолютных дат
            this.currentDateTemplate = this.switchDateTemplate([start, end]);
            this.periodTemplate = this.currentDateTemplate;

            if (this.isCompareSync) {
                this.setCompareRange(start, end);
            }
        }
    }

    setCompareRange(start: string, end: string, day?: string): void {
        // Та же логика, по которой сохранённый отчёт разворачивает период сравнения,
        // см. @/utils/comparePeriod. Держать её в двух местах нельзя: разъезжается.
        let compareRange: ComparePeriod;

        if (isFullCalendarMonth(start, end)) {
            // Полный месяц сравнивается с полным месяцем: клик по дню выбирает его месяц.
            const monthsOffset = day ? Math.max(1, getCompareOffsetInMonths(start, day)) : 1;
            compareRange = getComparePeriodByMonths(start, monthsOffset);
        } else {
            compareRange = day
                ? getComparePeriodFromStart(start, end, day)
                : getComparePeriodByGap(start, end);
        }

        const [compareStart, compareEnd] = compareRange;

        this.compareFilterSync = [compareStart, compareEnd];

        if (this.isCompareInFocus) {
            setTimeout(() => {
                this.calendar.dragValue = null;
                this.calendar.value_ = { start: parseISO(compareStart), end: parseISO(compareEnd) };
            }, 100);
        }
    }

    getDateFilters(condition: string[]): string[] | void {
        return this.filterSync = condition;
    }

    async moveCalendar(date: string | Date): Promise<void> {
        await this.calendar.focusDate(date);
    }

    getDateTemplate(currentDateTemplate: string): string[] | void {
        switch (currentDateTemplate) {
        case DATE_TEMPLATE.TODAY:
            return this.getDateFilters([getCurrentDate('yyyy-MM-dd'), getCurrentDate('yyyy-MM-dd')]);
        case DATE_TEMPLATE.YESTERDAY:
            return this.getDateFilters([getPreviousDate('yyyy-MM-dd'), getPreviousDate('yyyy-MM-dd')]);
        case DATE_TEMPLATE.LAST_SEVEN_DAYS:
            return this.getDateFilters(getCurrentWeek('yyyy-MM-dd'));
        case DATE_TEMPLATE.CURRENT_WEEK:
            return this.getDateFilters(getFullCurrentWeek('yyyy-MM-dd'));
        case DATE_TEMPLATE.LAST_WEEK:
            return this.getDateFilters(getFullPreviousWeek('yyyy-MM-dd'));
        case DATE_TEMPLATE.CURRENT_MONTH:
            return this.getDateFilters(getFullCurrentMonth('yyyy-MM-dd'));
        case DATE_TEMPLATE.LAST_MONTH:
            return this.getDateFilters(getFullPreviousMonth('yyyy-MM-dd', this.allowedDates()!.start));
        case DATE_TEMPLATE.CURRENT_YEAR:
            return this.getDateFilters(getCurrentYear('yyyy-MM-dd', this.allowedDates()!.start));
        }
    }

    changeDateTemplate(currentDateTemplate: string): void {
        const date = this.getDateTemplate(currentDateTemplate);
        if (date && date[0]) {
            this.moveCalendar(date[0]);
            this.isCompareInFocus = false;
            this.setCompareRange(date[0], date[1]);
            this.periodTemplate = currentDateTemplate;
        }
    }

    switchDateTemplate(filter: any): string {
        switch (filter.join()) {
        case [getCurrentDate('yyyy-MM-dd')].join():
        case [getCurrentDate('yyyy-MM-dd'), getCurrentDate('yyyy-MM-dd')].join():
            return DATE_TEMPLATE.TODAY;
        case [getPreviousDate('yyyy-MM-dd')].join():
        case [getPreviousDate('yyyy-MM-dd'), getPreviousDate('yyyy-MM-dd')].join():
            return DATE_TEMPLATE.YESTERDAY;
        case getCurrentWeek('yyyy-MM-dd').join():
        case getCurrentWeek('yyyy-MM-dd').reverse().join():
            return DATE_TEMPLATE.LAST_SEVEN_DAYS;
        case getFullCurrentWeek('yyyy-MM-dd').join():
        case getFullCurrentWeek('yyyy-MM-dd').reverse().join():
            return DATE_TEMPLATE.CURRENT_WEEK;
        case getFullPreviousWeek('yyyy-MM-dd').join():
        case getFullPreviousWeek('yyyy-MM-dd').reverse().join():
            return DATE_TEMPLATE.LAST_WEEK;
        case getFullCurrentMonth('yyyy-MM-dd').join():
        case getFullCurrentMonth('yyyy-MM-dd').reverse().join():
            return DATE_TEMPLATE.CURRENT_MONTH;
        case getFullPreviousMonth('yyyy-MM-dd', this.allowedDates()!.start).join():
        case getFullPreviousMonth('yyyy-MM-dd', this.allowedDates()!.start).reverse().join():
            return DATE_TEMPLATE.LAST_MONTH;
        case getCurrentYear('yyyy-MM-dd', this.allowedDates()!.start).join():
        case getCurrentYear('yyyy-MM-dd', this.allowedDates()!.start).reverse().join():
            return DATE_TEMPLATE.CURRENT_YEAR;
        default:
            return DATE_TEMPLATE.RANDOM_PERIOD;
        }
    }
}
